import { useApplicationConfigStore } from '~/store/application-config'
import { storeToRefs } from 'pinia'

export function useNavItem() {
  const applicationConfigStore = useApplicationConfigStore()
  const { navItems, completedModuleEventPaths } = storeToRefs(applicationConfigStore)
  const currentRoute = useRoute()

  const activeRoute = computed(() => {
    const _curPath = currentRoute.path
    const _navItems = navItems?.value

    if (!_navItems) {
      return ''
    }

    return _navItems.reduce((result, route) => {
      // If we already have a result, escape out
      if (result !== '') {
        return result
      }

      // Check for direct match
      if (route.url === _curPath) {
        return route.url
      }

      // Visible children return their own url
      let visibleChildren = route.visibleChildren || []
      for (let child of visibleChildren) {
        if (_curPath === child.url) {
          return child.url
        }

        // use visible child url for nav sections with hidden children
        if (child?.navSectionLabel) {
          const hiddenChildren = child.hiddenChildren || []
          for (let hiddenChild of hiddenChildren) {
            if (_curPath === hiddenChild?.url) {
              return child.url
            }
          }
        }
      }

      // Hidden children return the parent's url
      let hiddenChildren = route.hiddenChildren || []
      for (let child of hiddenChildren) {
        if (_curPath === child.url) {
          return route.url
        }
      }

      return '' // Don't forget to return for the reduce to continue!
    }, '')
  })

  const activeParent = computed(() => {
    if (activeRoute.value === '') {
      return ''
    }

    const _curPath = currentRoute.path
    const _navItems = navItems?.value

    return _navItems.reduce((result, route) => {
      // If we already have a result, escape out
      if (result !== '') {
        return result
      }

      let visibleChildren = route.visibleChildren || []
      for (let child of visibleChildren) {
        if (_curPath === child.url) {
          return route.url
        }
      }

      return ''
    }, '')
  })

  const activeSectionLabel = computed(() => {
    const _curPath = currentRoute.path
    const _navItems = navItems?.value

    for (const navItem of _navItems) {
      if (!navItem?.visibleChildren?.length && !navItem?.hiddenChildren?.length) {
        continue
      }

      const visibleChildren = navItem.visibleChildren || []
      for (const visibleChild of visibleChildren) {
        if (!visibleChild?.navSectionLabel) {
          continue
        }
        const hiddenChildren = visibleChild.hiddenChildren || []
        const matchedChild = hiddenChildren.find(child =>
          _curPath === child?.url && child?.navSectionLabel
        )
        if (matchedChild) {
          return matchedChild.navSectionLabel
        }
      }
    }

    return ''
  })

  function isNavItemComplete(item) {
    if (!item?.visibleChildren?.length && !item?.hiddenChildren?.length) {
      return item.completed
    }

    return (item.completed || item?.type === 'section') && _getAllChildrenForNavItem(item)
      .every(child => {
        // adopted hiddenChildren don't have a completed property
        if(child?.completed === undefined) {
          return true
        }

        return child.completed === true
      })
  }

  function isNavItemCompleteOptimistic(item) {
    if (!item?.url) {
      return false
    }

    if (!item?.visibleChildren?.length && !item?.hiddenChildren?.length) {
      return completedModuleEventPaths.value.includes(item.url)
    }

    const parentComplete = completedModuleEventPaths.value.includes(item.url)

    return (parentComplete || item?.type === 'section') && _getAllChildrenForNavItem(item)
      .every(child => {
        // adopted hiddenChildren don't have a completed property
        if(child?.completed === undefined){
          return true
        }
        return completedModuleEventPaths.value.includes(child?.url)
      })
  }

  function _getAllChildrenForNavItem(navItem) {
    const allChildren = []

    if (navItem?.visibleChildren?.length) {
      allChildren.push(...navItem.visibleChildren)
      navItem.visibleChildren.forEach(child => {
        allChildren.push(..._getAllChildrenForNavItem(child))
      })
    }

    if (navItem?.hiddenChildren?.length) {
      allChildren.push(...navItem.hiddenChildren)
      navItem.hiddenChildren.forEach(child => {
        allChildren.push(..._getAllChildrenForNavItem(child))
      })
    }

    return allChildren
  }

  // Could possibly be an empty string
  const currentActiveVisibleChildLabel = computed(() => {
    const _curPath = currentRoute.path
    const _navItems = navItems?.value

    if (!_navItems) {
      return ''
    }

    return _navItems.reduce((result, route) => {
      if (result !== '') {
        return result
      }

      // Direct match returns nothing

      // Visible children return the child's label
      const visibleChildren = route.visibleChildren || []
      for (const child of visibleChildren) {

        // Hidden children of visible children return the visible child's label
        if (child?.navSectionLabel) {
          const hiddenChildren = child.hiddenChildren || []
          for (const hiddenChild of hiddenChildren) {
            if (_curPath === hiddenChild.url) {
              return child.label
            }
          }
        }

        if (_curPath === child.url) {
          return child.label
        }
      }

      // Hidden children return nothing

      return ''
    }, '')
  })

  // Unlikely, but could be an empty string
  const currentActiveTopLevelLabel = computed(() => {
    const _curPath = currentRoute.path
    const _navItems = navItems?.value
    if (!_navItems) {
      return ''
    }
    return _navItems.reduce((result, route) => {
      if (result !== '') {
        return result
      }

      // Check for direct match
      if (route.url === _curPath) {
        return route.label
      }

      // Children matches return the parent's label
      const visibleChildren = route.visibleChildren || []
      for (const child of visibleChildren) {

        // Hidden children of visible children return the parent's label too
        if (child?.navSectionLabel) {
          const hiddenChildren = child.hiddenChildren || []
          for (const hiddenChild of hiddenChildren) {
            if (_curPath === hiddenChild.url) {
              return route.label
            }
          }
        }

        if (_curPath === child.url) {
          return route.label
        }
      }

      const hiddenChildren = route.hiddenChildren || []
      for (const child of hiddenChildren) {
        if (_curPath === child.url) {
          return route.label
        }
      }

      return ''
    }, '')
  })

  return { activeRoute, activeParent, activeSectionLabel, currentActiveTopLevelLabel, currentActiveVisibleChildLabel, isNavItemComplete, isNavItemCompleteOptimistic }
}
